import type { AccessState } from '@bbbk/schema';
import { ApiError } from '../lib/api';

/**
 * Does this dead link offer a way on, or only a phone call?
 *
 * A SPENT, EXPIRED OR REVOKED LINK IS EXPLAINED, THEN OFFERS SIGN-IN. The person
 * holding an old link is usually somebody who already registered and kept the
 * only Beeches Brook URL they ever had; signing in puts them on their stays.
 * A revoked one is shown the same screen as an expired one (Freddi,
 * 2026-09-14) — it says nothing about the desk having acted, and sign-in
 * refuses the account anyway, which is the right outcome.
 *
 * ONE STATE DOES NOT: `none` — nobody issued this token. A stranger guessing
 * tokens learns nothing from a sign-in button, and somebody who mistyped their
 * own link needs to be told it is not one of ours.
 */
export function offersSignIn(state: AccessState): boolean {
  return state === 'expired' || state === 'part' || state === 'revoked';
}

/** The customer's own sign-in URL — a durable way back in, with no link needed. */
export const CUSTOMER_SIGNIN_PATH = '/signin';

/** What the door shows when the invite lookup itself failed. */
export type DoorFailure = 'missing' | 'busy' | 'unreachable';

/**
 * Three answers, because there are three different facts.
 *
 * A 429 is the edge limit, not the link: the connection is hot and the link is
 * probably fine, so the door says wait. A 404 is a token nobody issued —
 * *not one of ours*.
 *
 * ANYTHING ELSE IS NEITHER. It used to land on the 404 page, on the reasoning
 * that the customer's next move was the same either way. It is not: a 500 or a
 * dropped connection says nothing about their link, and telling somebody
 * holding a perfectly good one that it is not ours — with no retry — is the
 * exact mistake `CustomerLockedOut`'s own header warns about (P2.21,
 * 2026-09-15). Never assert a cause the response has not established.
 */
export function doorReplyFromError(e: unknown): DoorFailure {
  if (e instanceof ApiError && e.status === 429) return 'busy';
  if (e instanceof ApiError && e.status === 404) return 'missing';
  return 'unreachable';
}
